// src/components/QueueFlow.js
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import ReactFlow, {
  Background,
  Controls,
  Handle,
  Position,
  useEdgesState,
  useNodesState,
} from "reactflow";
import "reactflow/dist/style.css";
import { Button, Space, Tag, Tooltip, Typography, message } from "antd";

const { Text } = Typography;

const ROOT_ID = "root";
const NODE_W = 320;
const NODE_H = 120;

function dist(a, b) {
  const dx = a.x - b.x;
  const dy = a.y - b.y;
  return Math.sqrt(dx * dx + dy * dy);
}
function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function formatBRL(v) {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  if (Number.isNaN(n)) return null;
  return n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

// ===== Nodes =====
function RootNode({ data }) {
  return (
    <div
      style={{
        width: 260,
        padding: 14,
        borderRadius: 18,
        border: "1px solid rgba(255,255,255,.14)",
        background: "linear-gradient(180deg, #141416, #0d0d0e)",
        boxShadow: "0 18px 50px rgba(0,0,0,.5)",
        color: "white",
        textAlign: "center",
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 800, letterSpacing: 0.3 }}>
        {data?.title || "Fila do dia"}
      </div>

      <div style={{ marginTop: 8 }}>
        <Tag color="geekblue" style={{ borderRadius: 999, padding: "2px 10px" }}>
          Prioridade = mais perto do centro
        </Tag>
      </div>

      <Handle type="source" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Right} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Left} style={{ opacity: 0 }} />
    </div>
  );
}

function ProductNode({ data }) {
  const { item, order, onEdit, onDelete } = data;

  const pFrom = formatBRL(item.priceFrom);
  const pTo = formatBRL(item.priceTo);

  return (
    <div
      onDoubleClick={() => onEdit(item)}
      style={{
        width: NODE_W,
        minHeight: NODE_H,
        padding: 12,
        borderRadius: 16,
        border: "1px solid rgba(255,255,255,.12)",
        background: "rgba(15,15,16,.92)",
        boxShadow: "0 18px 55px rgba(0,0,0,.55)",
        color: "white",
        cursor: "grab",
      }}
    >
      <Handle type="target" position={Position.Top} style={{ opacity: 0 }} />
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0 }} />

      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
        <div
          style={{
            width: 26,
            height: 26,
            borderRadius: 999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(125,176,255,.15)",
            border: "1px solid rgba(125,176,255,.35)",
            color: "rgba(255,255,255,.9)",
            fontWeight: 800,
            fontSize: 12,
            flexShrink: 0,
          }}
        >
          {order || "—"}
        </div>

        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontWeight: 850, color: "rgba(255,255,255,.9)" }}>
            {item.productName}
          </div>

          {item.description && (
            <div
              style={{
                marginTop: 6,
                fontSize: 12,
                color: "rgba(255,255,255,.66)",
                whiteSpace: "pre-wrap",
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
              title={item.description}
            >
              {item.description}
            </div>
          )}

          {(pFrom || pTo) && (
            <div style={{ marginTop: 6, fontSize: 12, color: "rgba(255,255,255,.78)" }}>
              {pFrom && <span>de <b>{pFrom}</b></span>}
              {pTo && <span> &nbsp;por <b>{pTo}</b></span>}
            </div>
          )}

          <div style={{ marginTop: 6 }}>
            <a
              href={item.productUrl}
              target="_blank"
              rel="noreferrer"
              style={{ color: "#7db0ff", wordBreak: "break-all", fontSize: 12 }}
            >
              {item.productUrl}
            </a>
          </div>

          <div style={{ marginTop: 10 }}>
            <Tag
              style={{
                borderRadius: 999,
                padding: "0 10px",
                border: "1px solid rgba(255,255,255,.10)",
                background: "rgba(255,255,255,.04)",
                color: "rgba(255,255,255,.75)",
              }}
            >
              status: {item.status || "READY"}
            </Tag>
            <Tag
              style={{
                borderRadius: 999,
                padding: "0 10px",
                border: "1px solid rgba(255,255,255,.10)",
                background: "rgba(255,255,255,.04)",
                color: "rgba(255,255,255,.75)",
              }}
            >
              duplo clique = editar
            </Tag>
          </div>
        </div>

        <Space direction="vertical" size={6}>
          <Tooltip title="Editar">
            <Button size="small" onClick={() => onEdit(item)}>
              ✏️
            </Button>
          </Tooltip>
          <Tooltip title="Deletar">
            <Button danger size="small" onClick={() => onDelete(item)}>
              🗑️
            </Button>
          </Tooltip>
        </Space>
      </div>
    </div>
  );
}

const nodeTypes = { rootNode: RootNode, productNode: ProductNode };

export default function QueueFlowMindMap({
  day,
  items,
  onEdit,
  onDelete,
  onSaveOrder, // async (orderedIds, positions) => Promise
}) {
  const rfRef = useRef(null);

  const savingRef = useRef(false);
  const lastSavedRef = useRef("");
  const debounceTimerRef = useRef(null);

  const initial = useMemo(() => {
    const root = {
      id: ROOT_ID,
      type: "rootNode",
      position: { x: 380, y: 200 },
      draggable: false,
      data: { title: `Fila — ${day}` },
    };

    const radius = 320;
    const angleStep = (Math.PI * 2) / Math.max(items.length, 1);

    const productNodes = items.map((item, idx) => {
      const hasPos = typeof item.posX === "number" && typeof item.posY === "number";
      const angle = idx * angleStep;
      const fallbackX = Math.cos(angle) * radius + 380;
      const fallbackY = Math.sin(angle) * radius + 200;

      const x = hasPos ? item.posX : fallbackX;
      const y = hasPos ? item.posY : fallbackY;

      return {
        id: item.id,
        type: "productNode",
        position: { x, y },
        data: { item, onEdit, onDelete },
      };
    });

    const edges = items.map((item) => ({
      id: `e-${ROOT_ID}-${item.id}`,
      source: ROOT_ID,
      target: item.id,
      animated: true,
      type: "smoothstep",
      style: {
        stroke: "#b6ff00",
        strokeWidth: 2,
        strokeOpacity: 0.55,
        filter: "drop-shadow(0 0 6px rgba(182,255,0,.55))",
      },
    }));

    return { nodes: [root, ...productNodes], edges };
  }, [items, day, onEdit, onDelete]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initial.nodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initial.edges);

  useEffect(() => {
    setNodes(initial.nodes);
    setEdges(initial.edges);
    lastSavedRef.current = "";
  }, [initial, setNodes, setEdges]);

  const computedOrder = useMemo(() => {
    const root = nodes.find((n) => n.id === ROOT_ID);
    if (!root) return [];

    return nodes
      .filter((n) => n.type === "productNode")
      .map((n) => ({ id: n.id, d: dist(n.position, root.position) }))
      .sort((a, b) => a.d - b.d)
      .map((x) => x.id);
  }, [nodes]);

  const orderMap = useMemo(() => {
    return Object.fromEntries(computedOrder.map((id, i) => [id, i + 1]));
  }, [computedOrder]);

  const renderNodes = useMemo(() => {
    return nodes.map((n) => {
      if (n.type !== "productNode") return n;
      return {
        ...n,
        data: {
          ...n.data,
          order: orderMap[n.id] || null,
          onEdit,
          onDelete,
        },
      };
    });
  }, [nodes, orderMap, onEdit, onDelete]);

  const getPositionsMap = useCallback(() => {
    const map = {};
    nodes
      .filter((n) => n.type === "productNode")
      .forEach((n) => {
        map[n.id] = { x: n.position.x, y: n.position.y };
      });
    return map;
  }, [nodes]);

  const saveOrder = useCallback(
    async (orderedIds, { silent = false } = {}) => {
      const fp = orderedIds.join(",");
      if (fp === lastSavedRef.current) return;
      if (savingRef.current) return;

      savingRef.current = true;

      try {
        const positions = getPositionsMap();
        await onSaveOrder(orderedIds, positions);
        lastSavedRef.current = fp;
        if (!silent) message.success("Ordem + layout salvos ✅");
      } catch (e) {
        if (!silent) message.error("Falha ao salvar ordem/layout. Verifique /queue/reorder.");
      } finally {
        savingRef.current = false;
      }
    },
    [onSaveOrder, getPositionsMap]
  );

  const onNodeDragStop = useCallback(
    (_, node) => {
      if (node.id === ROOT_ID) return;

      setNodes((nds) =>
        nds.map((n) => {
          if (n.id !== node.id) return n;
          return {
            ...n,
            position: {
              x: clamp(n.position.x, -1200, 2200),
              y: clamp(n.position.y, -1200, 2200),
            },
          };
        })
      );

      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        saveOrder(computedOrder, { silent: true });
      }, 600);
    },
    [setNodes, saveOrder, computedOrder]
  );

  const handleSave = async () => {
    await saveOrder(computedOrder, { silent: false });
  };

  return (
    <div
      style={{
        height: "78vh",
        borderRadius: 16,
        overflow: "hidden",
        border: "1px solid rgba(255,255,255,.08)",
        position: "relative",
      }}
    >
      <div
        style={{
          position: "absolute",
          zIndex: 10,
          padding: 10,
          margin: 12,
          borderRadius: 14,
          border: "1px solid rgba(255,255,255,.10)",
          background: "rgba(10,10,11,.72)",
          backdropFilter: "blur(8px)",
        }}
      >
        <Space>
          <Button type="primary" onClick={handleSave}>
            💾 Salvar ordem
          </Button>
          <Text style={{ color: "rgba(255,255,255,.75)" }}>
            Arrasta: mais perto do centro = maior prioridade.
          </Text>
        </Space>
      </div>

      <ReactFlow
        ref={rfRef}
        nodes={renderNodes}
        edges={edges}
        nodeTypes={nodeTypes}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeDragStop={onNodeDragStop}
        nodesDraggable
        nodesConnectable={false}
        panOnScroll
        fitView
        minZoom={0.2}
        maxZoom={2}
      >
        <Background />
        <Controls />
      </ReactFlow>
    </div>
  );
}
